import * as React from 'react';
import styled from 'styled-components';
import axios from 'axios';
import swal from 'sweetalert2';
import errorReporter from '@app/errorReporter';
import { Modal } from 'components';
import { getLocalTimeZone } from 'utils';
import { BasicContact } from '@bb/domain/dist/types';

import NewTourForm from './NewTourForm';

interface VirtualTourModalProps {
  coop: BasicContact;
  visible: boolean;
  closeModal: (requireUpdate?: boolean) => void;
  dashboardId: string;
  retsId: string;
  setCreatedRoom: (room: string) => void;
}

const VirtualTourModal: React.FC<VirtualTourModalProps> = ({
  coop,
  visible,
  closeModal,
  dashboardId,
  retsId,
  setCreatedRoom,
}) => {
  const [tourName, setTourName] = React.useState('');
  const [tourDate, setTourDate] = React.useState(null);
  const [isLoading, setIsLoading] = React.useState(false);
  const [isError, setIsError] = React.useState(false);

  const clearState = () => {
    setTourName('');
    setTourDate(null);
  };

  const submitHandler = async () => {
    try {
      setIsLoading(true);
      const newTour = {
        tourName,
        startTime: tourDate,
        coop,
        timezone: getLocalTimeZone(),
      };
      const isExternal = retsId || dashboardId;
      const { data } = await axios.post(
        `${isExternal ? '/external/virtual_tour' : '/virtual_tour'}`,
        {
          newTour,
          dashboardId,
          retsId,
        },
      );
      swal.fire({
        type: 'success',
        title: 'Virtual Tour created',
      });
      setCreatedRoom(data);
      closeModal(true);
      clearState();
    } catch (err) {
      errorReporter.error(`Error creating a virtual tour: ${err}`);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Modal
      open={visible}
      onCancel={() => {
        closeModal();
        clearState();
      }}
      okText={'Save Tour'}
      onOk={submitHandler}
      okButtonProps={{
        disabled: !tourName || !tourDate || isError,
        loading: isLoading,
        ...{
          'data-cy': 'create-new-save',
        },
      }}
      cancelButtonProps={
        {
          'data-cy': 'create-new-cancel',
        } as Record<string, string>
      }
    >
      <CenteredModalTitle>Create New Tour</CenteredModalTitle>
      <NewTourForm
        setIsError={setIsError}
        setTourName={setTourName}
        setTourDate={setTourDate}
        tourDate={tourDate}
        tourName={tourName}
      />
    </Modal>
  );
};

export default VirtualTourModal;

const CenteredModalTitle = styled.h3`
  text-align: center;
  padding: 20px;
`;
